import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '#pplx/frontend/aether/components/Button/index.js';
import { useColorScheme } from '#pplx/frontend/aether/hooks/useColorScheme.js';
import { resolveSpaceIcon } from '#pplx/frontend/apps/ask/collections/resolveSpaceIcon.js';
import { SpaceIconGlyph } from '#pplx/frontend/apps/ask/collections/SpaceIconGlyph.js';
import { sidebarProjectRetrySelectedEvent } from '#pplx/frontend/apps/ask/events/sidebar/analytics.js';
import { sidebarProjectListControlSelectedEvent, sidebarSubmenuLinkClickedEvent, } from '#pplx/frontend/apps/ask/events/sidebar/analytics.js';
import { usePrefetchCollection } from '#pplx/frontend/apps/ask/hooks/collections-hooks/usePrefetchCollection.js';
import { usePrefetchProjectSessions } from '#pplx/frontend/apps/ask/hooks/collections-hooks/usePrefetchProjectSessions.js';
import { useSidebarV2 } from '#pplx/frontend/apps/ask/hooks/sidebar-graphql-v2/context.js';
import { useAddOrMoveToProjectFactory } from '#pplx/frontend/apps/ask/hooks/sidebar-thread-actions/useAddOrMoveToProject.js';
import { useDeleteThreadConfirmation } from '#pplx/frontend/apps/ask/hooks/threads-hooks/useDeleteThreadConfirmation.js';
import { usePinnedItemsQuery } from '#pplx/frontend/apps/ask/hooks/threads-hooks/usePinnedItemsQuery.js';
import { useSidebarThreadActions } from '#pplx/frontend/apps/ask/hooks/threads-hooks/useSidebarThreadActions.js';
import { Empty } from '#pplx/frontend/apps/ask/layout/sidebar/core/lite/internal/Empty.js';
import { Item } from '#pplx/frontend/apps/ask/layout/sidebar/core/lite/internal/Item.js';
import { AnimatedMeasuredHeightContent } from '#pplx/frontend/apps/ask/layout/sidebar/core/lite/internal/Section.js';
import { ThreadHoverDetails } from '#pplx/frontend/apps/ask/layout/sidebar/core/lite/internal/ThreadHoverDetails.js';
import { ProjectListExpandCollapseButton } from '#pplx/frontend/apps/ask/layout/sidebar/core/lite/ProjectListExpandCollapseButton.js';
import { getSpaceThreadActionInput } from '#pplx/frontend/apps/ask/layout/sidebar/core/lite/spaceThreadActionInput.js';
import { useSidebarProjects } from '#pplx/frontend/apps/ask/layout/sidebar/core/lite/useSidebarProjects.js';
import { useSpaceExpansion } from '#pplx/frontend/apps/ask/layout/sidebar/core/lite/useSpaceExpansion.js';
import { useSpaceThreadDeletion } from '#pplx/frontend/apps/ask/layout/sidebar/core/lite/useSpaceThreadDeletion.js';
import { useSpaceThreads } from '#pplx/frontend/apps/ask/layout/sidebar/core/lite/useSpaceThreads.js';
import { getDisplayTitle } from '#pplx/frontend/apps/ask/markdown/markdown.js';
import { routes } from '#pplx/frontend/apps/ask/routing/spaRoutes.js';
import { prefetchSpaceProjectThreads, projectThreadsSessionKey, } from '#pplx/frontend/apps/ask/spaces-redesign/graphql/spaceProjectThreadsScopeCache.js';
import { getSpaceThreadUrl } from '#pplx/frontend/apps/ask/spaces-redesign/thread-utils/threadUtils.js';
import { getValidPinnedThreadContextUuid } from '#pplx/frontend/apps/ask/thread-list/pinnedThreadItem.js';
import { useThreadPinActions } from '#pplx/frontend/apps/ask/thread-list/useThreadPinAction.js';
import { useSession } from '#pplx/frontend/auth/useSession.js';
import { EMPTY_ARRAY } from '#pplx/frontend/common/defaults.js';
import { Dynamic } from '#pplx/frontend/dynamic/Dynamic.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import { useRouter } from '#pplx/frontend/router/provider.js';
import { usePathname } from '#pplx/frontend/router/usePathname.js';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, defineMessage, useIntl } from 'react-intl';
const GraphQLProjectThreads = Dynamic(async () => ({
    default: (await import('#pplx/frontend/apps/ask/layout/sidebar/core/lite/internal/GraphQLProjectThreads.js')).GraphQLProjectThreads,
}));
const LazySidebarProjectActionsMenu = Dynamic(async () => ({
    default: (await import('#pplx/frontend/apps/ask/layout/sidebar/core/lite/internal/SidebarProjectActionsMenu.js')).SidebarProjectActionsMenu,
}), { loading: () => _jsx("div", { className: "size-6 shrink-0" }) });
void LazySidebarProjectActionsMenu.preload();
const NO_THREADS_MESSAGE = defineMessage({
    defaultMessage: 'No sessions',
    description: 'Empty state shown under a project in the sidebar when it has no visible threads',
    id: 'cb8088e077',
});
const NO_PROJECTS_MESSAGE = defineMessage({
    defaultMessage: 'No projects',
    description: 'Empty state message when the user has no projects in the sidebar',
    id: '410446c86a',
});
const NO_PINNED_PROJECTS_MESSAGE = defineMessage({
    defaultMessage: 'No pinned projects',
    description: 'Empty state message when the user has no pinned projects',
    id: '772012b3e8',
});
const SHOW_MORE_PROJECTS_MESSAGE = defineMessage({
    defaultMessage: 'Show more',
    description: 'Sidebar button that expands the project list',
    id: 'c871e1c566',
});
const SHOW_LESS_PROJECTS_MESSAGE = defineMessage({
    defaultMessage: 'Show less',
    description: 'Sidebar button that collapses the project list to its initial size',
    id: '54cc15b191',
});
const THREADS_COULD_NOT_LOAD_MESSAGE = defineMessage({
    defaultMessage: 'Sessions could not load',
    description: 'Error state shown under a project in the sidebar when its threads fail to load',
    id: '1bc5c42c74',
});
function getSpaceKey(space) {
    return space.uuid || space.slug || space.title;
}
function getPromotedSpaceIcon(iconPresentation, isExpanded) {
    if (iconPresentation.type === 'folder') {
        // Two literal calls: the sprite extractor rejects dynamic `$icon()` args.
        return isExpanded ? $icon('folder-open') : $icon('folder');
    }
    return (_jsx(SpaceIconGlyph, { presentation: iconPresentation, iconSize: "small", emojiSize: "tiny", appearanceClassName: "flex size-4 items-center justify-center" }));
}
export const PromotedSpaces = ({ isSidebarOpen, isInPopover, collapsed, defaultSpaceExpanded = true, useContainerGap = false, foreground, onProjectListLoaded, emptyStateVariant, showPinnedOnly = false, inlineCreate, anchoredProjects = EMPTY_ARRAY, }) => {
    const sidebarV2 = useSidebarV2();
    if (sidebarV2)
        void GraphQLProjectThreads.preload();
    const defaultExpanded = sidebarV2 ? false : defaultSpaceExpanded;
    const { track } = useInstrumentation();
    const { status: sessionStatus } = useSession();
    const anchoredSpaces = useMemo(() => anchoredProjects.map((project) => ({
        ...project,
        collection: project,
        is_pinned: false,
        // Retained rows deliberately route by authoritative UUID across canonical refreshes.
        slug: project.uuid,
        visual_concepts: null,
    })), [anchoredProjects]);
    const { projects, isLoading, isExpanded, canToggleExpanded, toggleExpanded } = useSidebarProjects({
        enabled: sessionStatus === 'authenticated',
        showPinnedOnly,
        anchoredProjects: anchoredSpaces,
        reserveCreateSlot: inlineCreate != null,
    });
    const { $t } = useIntl();
    const { expandedBySpaceKey, setExpanded: handleToggleSpace, trackToggle, } = useSpaceExpansion({
        defaultExpanded,
    });
    const sidebarSpaces = useMemo(() => projects.flatMap((space) => space.slug
        ? [
            {
                ...space,
                link: routes['/projects/:slug'].href({ path: { slug: space.uuid } }),
            },
        ]
        : []), [projects]);
    const projectListPlacement = emptyStateVariant === 'computerInbox'
        ? 'computerInbox'
        : isInPopover
            ? 'collapsedPopover'
            : 'sidebar';
    const handleToggleProjectList = () => {
        const action = isExpanded ? 'showLess' : 'showMore';
        track(sidebarProjectListControlSelectedEvent({
            name: 'sidebar project list control selected',
            data: {
                action,
                placement: projectListPlacement,
                visibleProjectCount: sidebarSpaces.length,
            },
        }));
        toggleExpanded();
    };
    useEffect(() => {
        if (!isLoading)
            onProjectListLoaded?.();
    }, [isLoading, onProjectListLoaded]);
    if (sessionStatus === 'loading' || (isLoading && anchoredSpaces.length === 0)) {
        return inlineCreate?.row;
    }
    const projectRetry = sidebarV2?.projectsError ? (_jsx(Button, { variant: "text", size: "small", onClick: sidebarV2.retry, track: sidebarProjectRetrySelectedEvent({
            name: 'sidebar project retry selected',
            data: {
                placement: projectListPlacement,
                queryState: 'error',
                hasData: sidebarV2.projectsHasData,
            },
        }), children: _jsx(FormattedMessage, { defaultMessage: "Retry loading projects", id: "e74bdb3001", description: "Retry failed sidebar project loading" }) })) : null;
    if (projectRetry && sidebarSpaces.length === 0)
        return projectRetry;
    if (sidebarSpaces.length === 0 && inlineCreate == null) {
        if (showPinnedOnly) {
            return _jsx(Empty, { text: $t(NO_PINNED_PROJECTS_MESSAGE), variant: emptyStateVariant });
        }
        if (emptyStateVariant !== 'computerInbox')
            return null;
        return _jsx(Empty, { text: $t(NO_PROJECTS_MESSAGE), variant: emptyStateVariant });
    }
    return (_jsxs(_Fragment, { children: [projectRetry, inlineCreate?.row, sidebarSpaces.map((space) => (_jsx(PromotedSpaceGroup, { space: space, isPinned: space.space_type === 'pinned' || space.is_pinned === true, isSidebarOpen: isSidebarOpen, isInPopover: isInPopover, collapsed: collapsed, isExpanded: expandedBySpaceKey[getSpaceKey(space)] ?? defaultExpanded, onToggleSpace: handleToggleSpace, trackToggle: trackToggle, useContainerGap: useContainerGap, foreground: foreground }, getSpaceKey(space)))), canToggleExpanded && (_jsx("div", { className: useContainerGap ? undefined : 'py-[0.5px]', children: _jsx(ProjectListExpandCollapseButton, { label: $t(isExpanded ? SHOW_LESS_PROJECTS_MESSAGE : SHOW_MORE_PROJECTS_MESSAGE), onClick: handleToggleProjectList, loading: sidebarV2?.projectsLoadingMore }) }))] }));
};
const PromotedSpaceGroup = memo(function PromotedSpaceGroup({ space, isPinned, isSidebarOpen, isInPopover, collapsed, isExpanded, onToggleSpace, trackToggle, useContainerGap, foreground, }) {
    const pathname = usePathname();
    const sidebarV2 = useSidebarV2();
    const spaceKey = getSpaceKey(space);
    const spaceSlug = space.slug;
    const prefetchCollection = usePrefetchCollection({
        collectionSlug: spaceSlug,
        reason: 'sidebar-pinned-project-preload',
    });
    const spaceUuid = space.uuid ?? '';
    const { session } = useSession();
    const sessionKey = projectThreadsSessionKey(session);
    const prefetchProjectSessions = usePrefetchProjectSessions({ spaceUuid });
    const prefetchProjectData = useCallback(() => {
        prefetchCollection();
        if (sidebarV2)
            return;
        prefetchProjectSessions();
        // The main list is GraphQL-served for every view a sidebar row navigates
        // to: rows render only for signed-in sessions and land on the unfiltered
        // non-template default view, so hover warms the GraphQL scope cache and
        // never the legacy REST list.
        prefetchSpaceProjectThreads({ spaceUuid, sessionKey });
    }, [sidebarV2, prefetchCollection, prefetchProjectSessions, sessionKey, spaceUuid]);
    const handleToggleCollapse = useCallback(() => {
        trackToggle(space.uuid ?? undefined, !isExpanded);
        onToggleSpace(spaceKey);
    }, [isExpanded, onToggleSpace, space.uuid, spaceKey, trackToggle]);
    const { colorScheme } = useColorScheme();
    const iconPresentation = resolveSpaceIcon({ appearance: space.appearance, emoji: space.emoji }, colorScheme);
    const icon = foreground
        ? isExpanded
            ? $icon('folder-open')
            : $icon('folder')
        : getPromotedSpaceIcon(iconPresentation, isExpanded);
    if (isInPopover) {
        return (_jsx("div", { className: useContainerGap ? undefined : 'py-[0.5px]', children: _jsx(Item, { analyticsText: "project", variant: "secondary", href: space.link, text: space.title, sidebarOpen: isSidebarOpen, isInPopover: true, selected: pathname === space.link, collapsed: collapsed, foreground: foreground, onFocus: prefetchProjectData, onPointerDown: prefetchProjectData, onPointerEnter: prefetchProjectData }) }));
    }
    const shouldRenderThreads = !collapsed;
    return (_jsxs("div", { className: useContainerGap ? undefined : 'py-[0.5px]', children: [foreground ? (_jsx(Item, { analyticsText: "project", variant: "secondary", href: space.link, text: space.title, leadingIcon: icon, sidebarOpen: isSidebarOpen, isInPopover: isInPopover, selected: pathname === space.link, collapsed: !isExpanded, onToggleCollapse: handleToggleCollapse, endControlBeforeCollapse: _jsx(LazySidebarProjectActionsMenu, { space: space, isPinned: isPinned }), foreground: foreground, onFocus: prefetchProjectData, onPointerDown: prefetchProjectData, onPointerEnter: prefetchProjectData })) : (_jsx(Item, { analyticsText: "project", variant: "primary", href: space.link, text: space.title, icon: icon, sidebarOpen: isSidebarOpen, isInPopover: isInPopover, selected: pathname === space.link, collapsed: !isExpanded, onToggleCollapse: handleToggleCollapse, foreground: foreground, onFocus: prefetchProjectData, onPointerDown: prefetchProjectData, onPointerEnter: prefetchProjectData })), shouldRenderThreads && (_jsx(AnimatedMeasuredHeightContent, { expanded: isExpanded, children: sidebarV2 ? (_jsx(GraphQLProjectThreads, { live: isExpanded && isSidebarOpen, project: space, isSidebarOpen: isSidebarOpen, isInPopover: isInPopover, collapsed: collapsed, foreground: foreground })) : (_jsx(PromotedSpaceThreads, { spaceUuid: space.uuid ?? '', spaceSlug: spaceSlug, project: space, isSidebarOpen: isSidebarOpen, isInPopover: isInPopover, collapsed: collapsed, foreground: foreground })) }))] }));
});
const PromotedSpaceThreads = ({ spaceUuid, spaceSlug, project, isSidebarOpen, isInPopover, collapsed, foreground }) => {
    const { threads: visibleThreads, isLoading: isLoadingThreads, isError: hasThreadError, } = useSpaceThreads({ spaceUuid, spaceSlug });
    return (_jsx(SpaceThreadList, { threads: visibleThreads, loading: isLoadingThreads, error: hasThreadError, spaceSlug, project, isSidebarOpen, isInPopover, collapsed, foreground }));
};
export const SpaceThreadList = ({ threads: visibleThreads, loading, error, spaceSlug, project, isSidebarOpen, isInPopover, collapsed, foreground, }) => {
    const pathname = usePathname();
    const router = useRouter();
    const { $t } = useIntl();
    const onDeleted = useCallback((href) => {
        if (href === pathname)
            router.push(routes['/'].href());
    }, [pathname, router]);
    const handleDeleteThread = useSpaceThreadDeletion({ spaceSlug, onDeleted });
    if (loading)
        return null;
    const hasThreadError = error && visibleThreads.length === 0;
    return (_jsxs("div", { className: "flex flex-col gap-px pb-4 pt-px", children: [hasThreadError && (_jsx(Empty, { text: $t(THREADS_COULD_NOT_LOAD_MESSAGE), variant: foreground ? 'computerInbox' : undefined, indent: foreground ? 'projectThread' : undefined })), !hasThreadError && visibleThreads.length === 0 && (_jsx(Empty, { text: $t(NO_THREADS_MESSAGE), variant: foreground ? 'computerInbox' : undefined, indent: foreground ? 'projectThread' : undefined })), !hasThreadError &&
                visibleThreads.map((thread) => (_jsx(SpaceThreadItem, { thread: thread, spaceSlug: spaceSlug, project: project, isSidebarOpen: isSidebarOpen, isInPopover: isInPopover, collapsed: collapsed, onDeleteThread: handleDeleteThread, foreground: foreground }, thread.context_uuid)))] }));
};
const SpaceThreadItem = memo(function SpaceThreadItem({ thread, spaceSlug, project, isSidebarOpen, isInPopover, collapsed, onDeleteThread, foreground, }) {
    const sidebarV2 = useSidebarV2();
    const pathname = usePathname();
    const href = getSpaceThreadUrl({
        spaceSlug,
        threadSlug: thread.slug,
        isComputerThread: thread.mode === 'asi',
        isArticleThread: thread.mode === 'article',
    });
    const displayTitle = getDisplayTitle(thread.title || thread.description || thread.slug);
    const { $t } = useIntl();
    const isThread = thread.mode !== 'article';
    const contextUUID = thread.context_uuid ?? undefined;
    const currentCollectionUuid = project.uuid ?? undefined;
    const { data: pinnedItems = EMPTY_ARRAY } = usePinnedItemsQuery({
        enabled: sidebarV2 == null && isThread && Boolean(contextUUID),
        reason: 'sidebar-project-thread-pin-actions',
    });
    const isPinned = sidebarV2
        ? sidebarV2.pins.data?.some((item) => item.sidebarItemType !== 'automation' && item.context_uuid === contextUUID) === true
        : pinnedItems.some((item) => getValidPinnedThreadContextUuid(item) === contextUUID);
    const { pinThreads, unpinThreads } = useThreadPinActions({
        surface: 'sidebar',
        threadType: thread.mode === 'asi' ? 'computer' : 'search',
    });
    const createAddOrMoveToProject = useAddOrMoveToProjectFactory();
    const [menuIsActive, setMenuIsActive] = useState(false);
    const onDelete = useCallback(() => onDeleteThread(thread, href), [href, onDeleteThread, thread]);
    const deleteConfirmation = useDeleteThreadConfirmation(displayTitle, thread.mode === 'asi' ? 'task' : 'thread');
    const { menuItems: baseMenuItems } = useSidebarThreadActions(getSpaceThreadActionInput({ thread, displayTitle, onDelete, deleteConfirmation }));
    const { track } = useInstrumentation();
    const menuItems = useMemo(() => {
        if (!isThread || !contextUUID)
            return baseMenuItems;
        return [
            {
                text: isPinned
                    ? $t({
                        defaultMessage: 'Unpin',
                        description: 'Context menu label to unpin a session from sidebar history',
                        id: 'c38436ceb0',
                    })
                    : $t({
                        defaultMessage: 'Pin',
                        description: 'Context menu label to pin a session from sidebar history',
                        id: 'b604080670',
                    }),
                icon: isPinned ? $icon('pinned-off') : $icon('pin'),
                onClick: () => (isPinned ? unpinThreads([{ contextUUID }]) : pinThreads([{ contextUUID }])),
            },
            {
                text: currentCollectionUuid
                    ? $t({
                        defaultMessage: 'Move to project',
                        description: 'Context menu label to move a sidebar thread to a different project',
                        id: '82879e630e',
                    })
                    : $t({
                        defaultMessage: 'Add to project',
                        description: 'Context menu label to add a sidebar thread to a project',
                        id: '562340e3f0',
                    }),
                icon: currentCollectionUuid ? $icon('arrows-shuffle') : $icon('plus'),
                onClick: createAddOrMoveToProject({
                    contextUUID,
                    entryUUID: thread.uuid,
                    project: currentCollectionUuid
                        ? {
                            kind: 'in-project',
                            collectionUUID: currentCollectionUuid,
                            collectionSlug: spaceSlug,
                        }
                        : { kind: 'none' },
                }),
            },
            ...baseMenuItems,
        ];
    }, [
        $t,
        baseMenuItems,
        contextUUID,
        currentCollectionUuid,
        createAddOrMoveToProject,
        isPinned,
        isThread,
        pinThreads,
        spaceSlug,
        thread.uuid,
        unpinThreads,
    ]);
    const handleHoverDetailsTitleClick = useCallback(() => {
        track(sidebarSubmenuLinkClickedEvent({
            name: 'sidebar submenu link clicked',
            data: {
                href: thread.mode === 'asi'
                    ? '/computer/tasks/:thread_slug'
                    : thread.mode === 'article'
                        ? '/page/:thread_slug'
                        : '/search/:thread_slug',
                text: 'thread-hover-details-title',
            },
        }));
    }, [thread.mode, track]);
    const itemElement = (_jsx(Item, { variant: "secondary", href: href, text: displayTitle, loading: sidebarV2 != null && thread.thread_status === 'pending', sidebarOpen: isSidebarOpen, isInPopover: isInPopover, selected: pathname === href, collapsed: collapsed, menuItems: menuItems, onMenuOpenChange: setMenuIsActive, alignTextWithLeadingIcon: foreground, foreground: foreground }));
    const sessionItem = (_jsx(ThreadHoverDetails, { triggerElement: itemElement, href: href, title: displayTitle, isComputerTask: thread.mode === 'asi', updatedAt: thread.last_query_datetime, answerPreview: thread.answer_preview, projectTitle: project.title, projectIcon: project, unread: thread.unread, sidebarOpen: isSidebarOpen, isInPopover: isInPopover, menuIsActive: menuIsActive, onTitleClick: handleHoverDetailsTitleClick }));
    return sessionItem;
});
//# sourceMappingURL=SpaceItems.js.map